import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Container, Col, Row } from 'react-bootstrap'
import { Paper } from '@mui/material'
import { useOutletContext } from 'react-router-dom'
import { MdCenterFocusStrong } from 'react-icons/md'
import { TbArrowsMinimize, TbArrowsMaximize } from "react-icons/tb"
import moment from 'moment'

import MapDisplay from './components/MapDisplay'
import AlertIcons from '../components/AlertIcons'

import DataTable from 'Shared/Components/DataTable'
import useFetchData from 'Shared/Hooks/useFetchData'
import useLoop from 'Shared/Hooks/useLoop'
import { apiUrl } from 'Shared/utils'
import { IconButtonComponent } from 'Shared/Components/Icon'

const SmartwatchOutdoor = () => {
  const { role, setPageLoading } = useOutletContext()

  const { t } = useTranslation()
  const { send } = useFetchData()

  const [data, setData] = useState([])
  const [minimize, setMinimize] = useState(false)

  const mapRef = useRef(null)
  const tableRef = useRef()

  const filterRef = useRef({
    "search": ""
  })

  const loadData = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/latest`,
      params: {
        "search": filterRef.current.search,
        "type": "outdoor",
      },
      returnType: "json"
    })

    if (body) {
      setData(
        body.features.map((i) => ({
          "online": (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
              <span style={{ height: "15px", width: "15px", backgroundColor: i["properties"]["online"] ? "green" : "red", borderRadius: "50%" }}></span>
            </div>
          ),
          "online_status": i["properties"]["online"],
          "device_id": i["properties"]["device_id"],
          "worker_id": i["properties"]["worker_id"],
          "worker_name": i["properties"]["worker_name"],
          "battery_level": i["properties"]["battery_level"],
          "body_temp": i["properties"]["body_temp"] != null ? <div className={i["properties"]["body_temp_alert"] ? 'text-alert' : "text-normal"}>{(role === 'pm' || role === 'admin') ? i["properties"]["body_temp"] : t(`smartwatch_bodyTemp_${i["properties"]["body_temp"]}`)}</div> : null,
          "heart_rate": i["properties"]["heart_rate"] != null ? <div className={i["properties"]["heart_rate_alert"] ? 'text-alert' : "text-normal"}>{(role === 'pm' || role === 'admin') ? i["properties"]["heart_rate"] : t(`smartwatch_heartRate_${i["properties"]["heart_rate"]}`)}</div> : null,
          "sos": i["properties"]["alerts"]["sos"],
          "alerts": i["properties"]["online"] ? <AlertIcons data={i["properties"]["alerts"]} /> : null,
          "last_update": i["properties"]["last_update"] != null ? moment(i["properties"]["last_update"]).format("YYYY-MM-DD HH:mm:ss") : null,
          "last_message": i["properties"]["last_message"] != null ? moment(i["properties"]["last_message"]).format("YYYY-MM-DD HH:mm:ss") : null
        }))
      )

      mapRef.current.updateData(body)
    }
  }

  const focus = (id, zoom) => {
    const feature = mapRef.current.getFeature(id)

    mapRef.current.zoomToFeature(id, feature, zoom)
  }

  const fetchMapExtent = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/map_extent`,
      returnType: "json"
    })

    if (body != null) {
      mapRef.current.setMapExtent(body)
    }
  }

  const fetchGeofencingZones = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/geofencing_zones`,
      returnType: "json"
    })

    mapRef.current.updateZone(body["zone_type"], body["zones"])
  }

  useEffect(() => {
    const init = async () => {
      setPageLoading(true)

      await Promise.all([
        loadData(),
        fetchMapExtent(),
        fetchGeofencingZones()
      ])

      setPageLoading(false)
    }

    init()
  }, [])

  useLoop(loadData, 10000)

  return (<>
  <div className="mainContainer" style={{ backgroundColor: "white", borderRadius: "15px" }}>
    <Container fluid className="mainContainer" style={{ flexGrow: 1 }}>      
      <Row style={{ height: minimize ? "100%" : "50%", marginBottom: "5px" }}>
        <Col xs={12} style={{ height: "100%", overflow: "hidden", padding: "5px 10px 5px 10px", display: "flex" }}>
          <div style={{ flex: 1 }}>
            <MapDisplay focus={focus} ref={mapRef}/>
          </div>

          <div style={{ height: "100%", display: "flex", flexFlow: "column", justifyContent: "center", alignItems: "center" }}>
            <div style={{ width: "180px", display: "grid", gap: "10px", paddingLeft: "10px", paddingRight: "5px", overflow: "auto" }}>
              <div style={{ display: "flex", width: "100%", height: "100px", flexFlow: "column", border: "2px solid black" }}>
                <div style={{ textAlign: "center", borderBottom: "2px solid black" }}>
                  <div style={{ padding: "5px" }}>Online</div>
                </div>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center", flexGrow: 1 }}>
                  <div>{data.filter((i) => i["online_status"]).length}</div>
                </div>
              </div>

              <div style={{ display: "flex", width: "100%", height: "100px", flexFlow: "column", border: "2px solid black" }}>
                <div style={{ textAlign: "center", borderBottom: "2px solid black" }}>
                  <div style={{ padding: "5px" }}>Offline</div>
                </div>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center", flexGrow: 1 }}>
                  <div>{data.filter((i) => !i["online_status"]).length}</div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {minimize ? (
        <Row style={{ marginTop: "5px", display: minimize ? "block" : "none" }}>
          <Col xs={12} style={{ height: "100%", padding: "5px 10px 10px 10px" }}>
            <div style={{ width: "100%", display: "flex", justifyContent: "end", paddingRight: "10px" }}>
              <IconButtonComponent onClick={() => setMinimize(false)}>
                <TbArrowsMaximize size={20}/>
              </IconButtonComponent>
            </div>
          </Col>
        </Row>
      ) : (
        <Row style={{ height: "50%", marginTop: "5px", display: minimize ? "none" : "block" }}>
          <Col xs={12} style={{ height: "100%", padding: "5px 10px 10px 10px" }}>
            <Paper elevation={0} sx={{ display: "flex", flexFlow: "column", height: "100%" }}>
              <DataTable
                data={data} count={null} ref={tableRef}
                columns={{
                  "online": { "label": t("general_online"), "sorting": false, "nowrap": true },
                  "worker_name": { "label": t("smartwatch_worker"), "sorting": false, "nowrap": true },
                  "battery_level": { "label": t("smartwatch_betteryLevel"), "sorting": false, "nowrap": true },
                  "body_temp": { "label": t("smartwatch_bodyTemp"), "sorting": false, "nowrap": true },
                  "heart_rate": { "label": t("smartwatch_heartRate"), "sorting": false, "nowrap": true },
                  "alerts": { "label": t("smartwatch_alerts"), "sorting": false, "nowrap": true },
                  "last_update": { "label": t("general_lastUpdate"), "sorting": false, "nowrap": true },
                  "last_message": { "label": t("general_lastMessage"), "sorting": false, "nowrap": true } 
                }}
                renderActions={(row) => { return [
                  {icon: <MdCenterFocusStrong size={20}/>, text: "Locate Worker", onClick: function(row) { focus(row["device_id"], true) }},
                ]}}
                appActions={() => { return [
                  {icon: <TbArrowsMinimize size={20}/>, text: t("general_minimize"), onClick: function() { setMinimize(true) }}
                ]}}
              />
            </Paper>
          </Col>
        </Row>
      )}
    </Container> 
  </div>
  </>)
}

export default SmartwatchOutdoor
